import React, { useState, useEffect, useRef } from 'react';
import CalendarView from '../components/CalendarView';
import OrganizationFilter from '../components/OrganizationFilter';
import EventListView from '../components/EventListView';
import EventMapView from '../components/EventMapView';
import config from '../config';
import featureFlags from '../config/featureFlags';
import { Link } from 'react-router-dom';
import { generateEventSlug, getLocalDateKey, parseEventSlug } from '../utils/eventUtils';
import { useSwipeable } from 'react-swipeable';
import { useTheme } from '../context/ThemeContext';

function Home() {
    const { theme, themeName, toggleTheme } = useTheme();

    // Get current month and year
    const now = new Date();   
    const thisMonth = now.getMonth() + 1;
    const thisYear = now.getFullYear();

    // Set initial view mode — persisted for returning users, defaults to week if returning, month for first visit
    const getInitialViewMode = () => {
        const saved = localStorage.getItem('lastViewMode');
        if (saved) {
            if (saved === 'month' && featureFlags.calendar.enabled) return 'month';
            if (saved === 'week' && featureFlags.calendar.enabled) return 'week';
            if (saved === 'list' && featureFlags.list.enabled) return 'list';
            if (saved === 'map' && featureFlags.map.enabled) return 'map';
        }
        // No saved view: returning users (have any other stored prefs) default to week
        const isReturningUser = localStorage.getItem('disclaimer-dismissed') !== null
            || localStorage.getItem('starredEvents') !== null
            || localStorage.getItem('hidePrivateEvents') !== null;
        if (isReturningUser && featureFlags.calendar.enabled) return 'week';
        // First-time visitors
        if (featureFlags.calendar.enabled) return 'month';
        if (featureFlags.list.enabled) return 'list';
        if (featureFlags.map.enabled) return 'map';
        return 'month';
    };

    // State management
    const [curMonth, setValue] = useState(thisMonth);
    const [curYear, setYear] = useState(thisYear);
    const [isLoading, setIsLoading] = useState(false);
    //const [isAnimating, setIsAnimating] = useState(false);
    //const [swipeDirection, setSwipeDirection] = useState(null);
    const [viewMode, setViewMode] = useState(getInitialViewMode());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [events, setEvents] = useState([]);
    const [combinedEvents, setCombinedEvents] = useState([]);
    const [selectedEventSlug, setSelectedEventSlug] = useState(null);
    const [pendingHash, setPendingHash] = useState(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    //const [shouldOpenModal, setShouldOpenModal] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('all');
    const [toast, setToast] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showWeekPicker, setShowWeekPicker] = useState(false);
    const [currentWeek, setCurrentWeek] = useState(() => {
        const today = new Date();
        const currentDay = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        return startOfWeek;
    });
    const toastTimeout = useRef(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showLegal, setShowLegal] = useState(false);
    const [isRightPaneOpen, setIsRightPaneOpen] = useState(false);
    const processedSlugRef = useRef(null);
    const [showStarredOnly, setShowStarredOnly] = useState(false);
    const [hidePrivateEvents, setHidePrivateEvents] = useState(
        () => localStorage.getItem('hidePrivateEvents') === 'true'
    );
    const [disclaimerDismissed, setDisclaimerDismissed] = useState(
        () => localStorage.getItem('disclaimer-dismissed') === 'true'
    );
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const disclaimerRef = useRef(null);

    // Helper function to fetch events for a specific month
    const fetchEventsForMonth = async (year, month) => {
        const cacheKey = `events-${year}-${month}`;
        const versionKey = 'events-api-version';
        const cachedData = localStorage.getItem(cacheKey);
        const cachedVersion = localStorage.getItem(versionKey);
        
        try {
            // Check API version first
            const versionResponse = await fetch(`${config.apiBaseUrl}${config.endpoints.events.version}`);
            const currentVersion = await versionResponse.text();

            if (cachedVersion !== currentVersion) {
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('events-')) {
                        localStorage.removeItem(key);
                    }
                });
                localStorage.setItem(versionKey, currentVersion);
            } else if (cachedData) {
                return JSON.parse(cachedData);
            }

            const response = await fetch(`${config.apiBaseUrl}${config.endpoints.events.monthly}/${year}/${month}`);
            const data = await response.json();
            localStorage.setItem(cacheKey, JSON.stringify(data));
            return data;
        } catch (error) {
            //console.error(`Error fetching events for ${month}/${year}:`, error);
            if (cachedData) {
                return JSON.parse(cachedData);
            }
            return [];
        }
    };

    // Fetch events for the current month
    useEffect(() => {
        const fetchEvents = async () => {
            setIsLoading(true);
            
            try {
                const data = await fetchEventsForMonth(curYear, curMonth);
                setEvents(data);
            } catch (error) {
                //console.error('Error fetching events:', error);
                setEvents([]);
            } finally {
                setIsLoading(false);
                setInitialLoadComplete(true);
            }
        }

        fetchEvents();
    }, [curMonth, curYear]);

    // Fetch combined events for cross-month weeks
    useEffect(() => {
        const fetchCombinedEvents = async () => {
            if (viewMode === 'week' && currentWeek) {
                const endOfWeek = new Date(currentWeek);
                endOfWeek.setDate(currentWeek.getDate() + 6);
                
                const startMonth = currentWeek.getMonth() + 1;
                const startYear = currentWeek.getFullYear();
                const endMonth = endOfWeek.getMonth() + 1;
                const endYear = endOfWeek.getFullYear();
                
                // Check if week spans multiple months
                if (startMonth !== endMonth || startYear !== endYear) {
                    setIsLoading(true);
                    try {
                        // Fetch events for both months
                        const [startMonthEvents, endMonthEvents] = await Promise.all([
                            fetchEventsForMonth(startYear, startMonth),
                            fetchEventsForMonth(endYear, endMonth)
                        ]);
                        
                        // Combine and filter events for the specific week
                        const weekStart = getLocalDateKey(currentWeek);
                        const weekEnd = getLocalDateKey(endOfWeek);
                        
                        const combined = [...startMonthEvents, ...endMonthEvents].filter(event => {
                            const eventDate = getLocalDateKey(event.startDate);
                            return eventDate >= weekStart && eventDate <= weekEnd;
                        });
                        
                        setCombinedEvents(combined);
                    } catch (error) {
                        //console.error('Error fetching combined events:', error);
                        setCombinedEvents([]);
                    } finally {
                        setIsLoading(false);
                    }
                } else {
                    // Week is within single month, clear combined events
                    setCombinedEvents([]);
                }
            } else {
                // Not in week view, clear combined events
                setCombinedEvents([]);
            }
        };

        fetchCombinedEvents();
    }, [viewMode, currentWeek, curMonth, curYear]);

    // Extract unique organizations whenever events change
    useEffect(() => {
        if (events && events.length > 0) {
            const orgs = [...new Set(events.map(event => event.organization))].sort();
            setOrganizations(orgs);
        } else {
            setOrganizations([]);
        }
    }, [events]);

    // Filter events based on selected organization, view mode, and starred filter
    const filteredEvents = (() => {
        let eventsToFilter = events;
        
        // Use combined events for week view if available
        if (viewMode === 'week' && combinedEvents.length > 0) {
            eventsToFilter = combinedEvents;
        }
        
        // Starred filter
        if (showStarredOnly) {
            const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
            eventsToFilter = eventsToFilter.filter(event => starred.includes(generateEventSlug(event)));
        }

        // Hide private events filter
        if (hidePrivateEvents) {
            eventsToFilter = eventsToFilter.filter(event => event.eventURL !== '/private-events');
        }

        return selectedOrg === 'all'
            ? eventsToFilter
            : eventsToFilter.filter(event => event.organization === selectedOrg);
    })();


        const isSwipeFromDayPane = (eventData) => {
                const target = eventData?.event?.target;
                return !!(target && target.closest && target.closest('[data-day-swipe-zone="true"]'));
        };

        const handlers = useSwipeable({
                onSwipedLeft: (eventData) => {
                    if (viewMode === 'week') return;
                    if (isSwipeFromDayPane(eventData)) return;
                    if (!isModalOpen && !isRightPaneOpen && window.innerWidth < 768) {
                        handleIncrement();
                    }
                },
                onSwipedRight: (eventData) => {
                    if (viewMode === 'week') return;
                    if (isSwipeFromDayPane(eventData)) return;
                    if (!isModalOpen && !isRightPaneOpen && window.innerWidth < 768) {
                        handleDecrement();
                    }
                },
        preventDefaultTouchmoveEvent: true,
        trackMouse: false
      });

    const handleHidePrivateChange = (val) => {
        localStorage.setItem('hidePrivateEvents', String(val));
        setHidePrivateEvents(val);
    };

    const dismissDisclaimer = () => {
        localStorage.setItem('disclaimer-dismissed', 'true');
        setDisclaimerDismissed(true);
        setShowDisclaimer(false);
    };

    // Close disclaimer popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (disclaimerRef.current && !disclaimerRef.current.contains(e.target)) {
                setShowDisclaimer(false);
            }
        };
        if (showDisclaimer) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDisclaimer]);

    // Show toast for 3 seconds
    const showToast = (message) => {
        setToast(message);
        if (toastTimeout.current) clearTimeout(toastTimeout.current);
        toastTimeout.current = setTimeout(() => setToast(null), 3000);
    };

    const clearSelectedEventSlug = () => {
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        //console.log('Cleared selected event slug');
    };

    // Expose showToast globally for child components
    useEffect(() => {
        window.showToast = showToast;

        return () => {
            delete window.showToast;

        };
    }, []);

    const gotoToday = () => {
        setValue(thisMonth);
        setYear(thisYear);
        setShowMonthPicker(false);
        
        // Reset to current week
        const today = new Date();
        const currentDay = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        setCurrentWeek(startOfWeek);
        setShowWeekPicker(false);
        
        // clear the # in the url
        window.history.pushState({}, '', window.location.pathname);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        
        // Close right pane on small screens
        if (window.innerWidth < 768) {
          setIsRightPaneOpen(false);
        }
    }
    
   
      const handleIncrement = () => {
        if (viewMode === 'week') {
          // Week navigation
          const newWeek = new Date(currentWeek);
          newWeek.setDate(newWeek.getDate() + 7);
          setCurrentWeek(newWeek);
          setShowWeekPicker(false);
          
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
          
          // Check if the new week spans a different month and update month/year if needed
          const endOfNewWeek = new Date(newWeek);
          endOfNewWeek.setDate(newWeek.getDate() + 6);
          const newWeekMonth = newWeek.getMonth() + 1;
          const newWeekYear = newWeek.getFullYear();
          const endWeekMonth = endOfNewWeek.getMonth() + 1;
          const endWeekYear = endOfNewWeek.getFullYear();
          
          // If the week spans multiple months, use the month that has more days in the week
          const daysInStartMonth = 7 - newWeek.getDay();
          const daysInEndMonth = endOfNewWeek.getDay() + 1;
          
          if (daysInStartMonth >= daysInEndMonth) {
            // More days in start month, or equal
            if (newWeekMonth !== curMonth || newWeekYear !== curYear) {
              setValue(newWeekMonth);
              setYear(newWeekYear);
            }
          } else {
            // More days in end month
            if (endWeekMonth !== curMonth || endWeekYear !== curYear) {
              setValue(endWeekMonth);
              setYear(endWeekYear);
            }
          }
        } else {
          // Month navigation
          setValue(prevValue => prevValue + 1);
          setShowMonthPicker(false);
          if (curMonth === 12) {
            setValue(1);
            setYear(prevValue => prevValue + 1);
          }
          
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
        }
      }
    
      const handleDecrement = () => {
        if (viewMode === 'week') {
          // Week navigation
          const newWeek = new Date(currentWeek);
          newWeek.setDate(newWeek.getDate() - 7);
          setCurrentWeek(newWeek);
          setShowWeekPicker(false);
 
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
          
          // Check if the new week spans a different month and update month/year if needed
          const endOfNewWeek = new Date(newWeek);
          endOfNewWeek.setDate(newWeek.getDate() + 6);
          const newWeekMonth = newWeek.getMonth() + 1;
          const newWeekYear = newWeek.getFullYear();
          const endWeekMonth = endOfNewWeek.getMonth() + 1;
          const endWeekYear = endOfNewWeek.getFullYear();
          
          // If the week spans multiple months, use the month that has more days in the week
          const daysInStartMonth = 7 - newWeek.getDay();
          const daysInEndMonth = endOfNewWeek.getDay() + 1;
          
          if (daysInStartMonth >= daysInEndMonth) {
            // More days in start month, or equal
            if (newWeekMonth !== curMonth || newWeekYear !== curYear) {
              setValue(newWeekMonth);
              setYear(newWeekYear);
            }
          } else {
            // More days in end month
            if (endWeekMonth !== curMonth || endWeekYear !== curYear) {
              setValue(endWeekMonth);
              setYear(endWeekYear);
            }
          }
        } else {
          // Month navigation
          setValue(prevValue => prevValue - 1);
          setShowMonthPicker(false);
          if (curMonth === 1) {
            setValue(12);
            setYear(prevValue => prevValue - 1);
          }
          
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
        }
      }

    // Watch for month/org changes and auto-switch if no events for org
    useEffect(() => {
        if (selectedOrg !== 'all' && !isLoading && initialLoadComplete) {
            const orgEvents = events.filter(event => event.organization === selectedOrg);
            if (orgEvents.length === 0) {
                showToast('No events this month for the selected organization, switching to All Organizations.');
                setSelectedOrg('all');
            }
        }
    }, [curMonth, curYear, selectedOrg, events, isLoading, initialLoadComplete]);

    // Handle slug navigation when events are loaded
    useEffect(() => {
        if (selectedEventSlug && initialLoadComplete) {
            // Prevent re-processing the same slug
            if (processedSlugRef.current === selectedEventSlug) {
                //console.log('Slug already processed, skipping:', selectedEventSlug);
                return;
            }
            
            //console.log('Processing slug navigation:', selectedEventSlug, 'Events loaded:', events.length);
            // Check both regular events and combined events
            const allEvents = viewMode === 'week' && combinedEvents.length > 0 
                ? [...events, ...combinedEvents] 
                : events;
            
            //console.log('Available events:', allEvents.length);
            
            if (allEvents.length > 0) {
                // Find the event by slug
                const targetEvent = allEvents.find(event => {
                    const eventSlug = generateEventSlug(event);
                    return eventSlug === selectedEventSlug;
                });

                if (targetEvent) {
                    //console.log('Found target event:', targetEvent.title);
                    // Mark this slug as processed
                    processedSlugRef.current = selectedEventSlug;
                    
                    // Navigate to the correct month/year if needed
                    const eventDate = new Date(targetEvent.startDate);
                    const eventMonth = eventDate.getMonth() + 1;
                    const eventYear = eventDate.getFullYear();

                    // Only navigate if we're not already on the correct month/year
                    if (eventMonth !== curMonth || eventYear !== curYear) {
                        //console.log(`Event is in different month: ${eventMonth}/${eventYear}, navigating...`);
                        setValue(eventMonth);
                        setYear(eventYear);
                        // The event will be selected after the month/year change triggers a new fetch
                        // Don't clear the slug yet - let it be processed again after navigation
                    } else {
                        //console.log('Event is in current month, selecting it');
                        // We're already on the correct month, select the event
                        setSelectedEvent(targetEvent);
                        // Don't clear the slug - let it persist for the selected event
                    }
                } else {
                    //console.log('Event not found in current events');
                    // Event not found in current month
                    // Check if we need to navigate to a different month based on the slug
                    const dateInfo = parseEventSlug(selectedEventSlug);
                    if (dateInfo && (dateInfo.month !== curMonth || dateInfo.year !== curYear)) {
                        //console.log(`Navigating to month from slug: ${dateInfo.month}/${dateInfo.year}`);
                        // Navigate to the month/year from the slug
                        setValue(dateInfo.month);
                        setYear(dateInfo.year);
                        // Keep the slug to be processed after the new events load
                    } else {
                        //console.log('Event not found and already on correct month, clearing slug');
                        // Event not found and we're already on the correct month, clear the slug
                        setSelectedEventSlug(null);
                        processedSlugRef.current = null;
                    }
                }
            } else {
                //console.log('No events loaded yet, waiting...');
                // Retry after a delay if events aren't loaded yet
                const retryTimer = setTimeout(() => {
                    if (selectedEventSlug && events.length === 0) {
                        //console.log('Retrying hash processing after delay...');
                        // Force a re-render to retry the processing
                        setSelectedEventSlug(selectedEventSlug);
                    }
                }, 2000);
                
                return () => clearTimeout(retryTimer);
            }
        }
    }, [selectedEventSlug, events, combinedEvents, viewMode, initialLoadComplete, curMonth, curYear]);

    // Process pending hash when events become available
    useEffect(() => {
        if (pendingHash && initialLoadComplete && events.length > 0) {
            //console.log('Processing pending hash:', pendingHash);
            setSelectedEventSlug(pendingHash);
            setPendingHash(null);
        }
    }, [pendingHash, initialLoadComplete, events.length]);

    // Process initial hash after app is fully loaded (backup mechanism)
    useEffect(() => {
        if (initialLoadComplete && !selectedEventSlug) {
            const hash = window.location.hash.slice(1);
            if (hash) {
                //console.log('Processing initial hash after app load:', hash);
                setSelectedEventSlug(hash);
            }
        }
    }, [initialLoadComplete, selectedEventSlug]);

    // Handle hash changes
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1);
            //console.log('Hash changed:', hash, 'Initial load complete:', initialLoadComplete, 'Events loaded:', events.length);
            if (hash) {
                // Parse the slug to extract year and month
                const dateInfo = parseEventSlug(hash);
                //console.log('Parsed date info:', dateInfo);
                if (dateInfo) {
                    // Navigate to the correct month/year first
                    if (dateInfo.month !== curMonth || dateInfo.year !== curYear) {
                        //console.log(`Navigating to ${dateInfo.month}/${dateInfo.year} from ${curMonth}/${curYear}`);
                        setValue(dateInfo.month);
                        setYear(dateInfo.year);
                        // The slug will be processed after the month/year change triggers a new fetch
                    }
                }
                
                // If events are loaded, process immediately; otherwise, store as pending
                if (initialLoadComplete && events.length > 0) {
                    setSelectedEventSlug(hash);
                } else {
                    //console.log('Events not loaded yet, storing hash as pending');
                    setPendingHash(hash);
                }
            } else {
                setSelectedEventSlug(null);
                setPendingHash(null);
            }
        };

        // Process initial hash immediately
        handleHashChange();
        
        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [curMonth, curYear, initialLoadComplete, events.length]);

        const setCurMonth = (month, options = {}) => {
                const { preserveRightPane = false } = options;
        setValue(month);
        // clear the # in the url
        window.history.pushState({}, '', window.location.pathname);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        
        // Close right pane on small screens
                if (window.innerWidth < 768 && !preserveRightPane) {
          setIsRightPaneOpen(false);
        }
      }

        const setCurYear = (year, options = {}) => {
                const { preserveRightPane = false } = options;
        setYear(year);
        // clear the # in the url
        window.history.pushState({}, '', window.location.pathname);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        
        // Close right pane on small screens
                if (window.innerWidth < 768 && !preserveRightPane) {
          setIsRightPaneOpen(false);
        }
    }

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
    };

    // View mode setters
    const setMonthView = () => {
        if (featureFlags.calendar.enabled) {
            setViewMode('month');
            localStorage.setItem('lastViewMode', 'month');
            setShowWeekPicker(false);
            setShowMonthPicker(false);
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };
    
    const setWeekView = () => {
        if (featureFlags.calendar.enabled) {
            setViewMode('week');
            localStorage.setItem('lastViewMode', 'week');
            // Set to the current week (today)
            const today = new Date();
            const startOfCurrentWeek = new Date(today);
            startOfCurrentWeek.setDate(today.getDate() - today.getDay());
            setCurrentWeek(startOfCurrentWeek);
            setShowMonthPicker(false);
            setShowWeekPicker(false);
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };

    const setListView = () => {
        if (featureFlags.list.enabled) {
            setViewMode('list');
            localStorage.setItem('lastViewMode', 'list');
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };
    
    const setMapView = () => {
        if (featureFlags.map.enabled) {
            setViewMode('map');
            localStorage.setItem('lastViewMode', 'map');
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };

    const renderMonthName = () => {
        const date = new Date(curYear, curMonth - 1, 1);
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const renderWeekRange = () => {
        const endOfWeek = new Date(currentWeek);
        endOfWeek.setDate(currentWeek.getDate() + 6);
        
        const startMonth = currentWeek.toLocaleString('default', { month: 'short' });
        const endMonth = endOfWeek.toLocaleString('default', { month: 'short' });
        const startDay = currentWeek.getDate();
        const endDay = endOfWeek.getDate();
        const year = currentWeek.getFullYear();
        
        if (startMonth === endMonth) {
            return `${startMonth} ${startDay}-${endDay}, ${year}`;
        } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
    };

    const getWeeksInMonth = (year, month) => {
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        const startOfFirstWeek = new Date(firstDay);
        startOfFirstWeek.setDate(firstDay.getDate() - firstDay.getDay());
        
        const weeks = [];
        let currentWeek = new Date(startOfFirstWeek);
        
        while (currentWeek <= lastDay || weeks.length < 6) {
            weeks.push(new Date(currentWeek));
            currentWeek.setDate(currentWeek.getDate() + 7);
        }
        
        return weeks;
    };

    return (
        <div className={`h-screen ${theme.background} ${theme.text}`}>
            <div className="h-full flex flex-col">
                <div className={`${theme.background} h-full flex flex-col`}>
                    {/* Header */}
                    <div className={`flex-none px-4 pb-2 border-b ${theme.border}`}>
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            {/* View Mode Toggle - Moved to navigation column */}
                        </div>

                        {/* Event Navigation */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mt-2">
                            {/* Column 1: Logo */}
                            <div className="hidden md:flex flex-col items-start">
                                <div className="text-2xl font-bold text-blue-600">
                                    <Link to="/" className="flex items-center">
                                        <img src="favicon-32x32.png" alt=" Events" className="h-8 w-8 object-contain" />
                                         <span className="flex text-center w-full ml-2 font-bold text-lg">ATLTech.events</span>
                                    </Link>
                                </div>
                                <div className="mt-5 text-sm text-gray-500 font-bold bg-yellow-600 text-white max-h-8 rounded-full px-2 py-1 sm:mb-0">
                                    BETA VERSION
                                </div>
                            </div>
                            
                            {/* Column 2: Navigation Controls */}
                            <div className="flex flex-col items-center gap-4 relative">
                                {/* View Mode Toggle */}
                                <div className="flex items-center gap-2">
                                    {featureFlags.calendar.enabled && (
                                        <>
                                            <button
                                                onClick={setMonthView}
                                                className={`py-2 px-3 rounded-lg transition flex flex-col items-center gap-0.5 ${
                                                    viewMode === 'month' ? theme.viewButtonActive : theme.viewButtonInactive
                                                }`}
                                                aria-label="Month view"
                                                title="Month view"
                                                aria-pressed={viewMode === 'month'}
                                            >
                                                <i className="fas fa-calendar-alt" aria-hidden="true"></i>
                                                <span className="text-xs leading-none">Month</span>
                                            </button>
                                            <button
                                                onClick={setWeekView}
                                                className={`py-2 px-3 rounded-lg transition flex flex-col items-center gap-0.5 ${
                                                    viewMode === 'week' ? theme.viewButtonActive : theme.viewButtonInactive
                                                }`}
                                                aria-label="Week view"
                                                title="Week view"
                                                aria-pressed={viewMode === 'week'}
                                            >
                                                <i className="fas fa-calendar-week" aria-hidden="true"></i>
                                                <span className="text-xs leading-none">Week</span>
                                            </button>
                                        </>
                                    )}

                                    {featureFlags.list.enabled && (
                                        <button
                                            onClick={setListView}
                                            className={`py-2 px-3 rounded-lg transition flex flex-col items-center gap-0.5 ${
                                                viewMode === 'list' ? theme.viewButtonActive : theme.viewButtonInactive
                                            }`}
                                            aria-label="List view"
                                            title="List view"
                                            aria-pressed={viewMode === 'list'}
                                        >
                                            <i className="fas fa-list" aria-hidden="true"></i>
                                            <span className="text-xs leading-none">List</span>
                                        </button>
                                    )}

                                    {featureFlags.map.enabled && (
                                        <button
                                            onClick={setMapView}
                                            className={`py-2 px-3 rounded-lg transition flex flex-col items-center gap-0.5 ${
                                                viewMode === 'map' ? theme.viewButtonActive : theme.viewButtonInactive
                                            }`}
                                            aria-label="Map view"
                                            title="Map view"
                                            aria-pressed={viewMode === 'map'}
                                        >
                                            <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                                            <span className="text-xs leading-none">Map</span>
                                        </button>
                                    )}

                                    <div className="relative" ref={disclaimerRef}>
                                        <button
                                            type="button"
                                            onClick={() => setShowDisclaimer(!showDisclaimer)}
                                            className={`py-2 px-3 rounded-lg transition flex flex-col items-center gap-0.5 ${theme.viewButtonInactive}`}
                                            aria-label="Event disclaimer"
                                            title="Event disclaimer"
                                        >
                                            <i className="fas fa-info-circle" aria-hidden="true"></i>
                                            <span className="text-xs leading-none">Info</span>
                                        </button>
                                        {showDisclaimer && (
                                            <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 ${theme.modalBg} border ${theme.border} rounded-lg shadow-lg p-3 z-50 text-sm ${theme.text}`}>
                                                Note: Events are subject to change. Please verify with the event host.
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        id="filter-button"
                                        type="button"
                                        onClick={() => setShowFilters(!showFilters)}
                                        className={`py-2 px-3 rounded-lg transition flex flex-col items-center gap-0.5 ${
                                            selectedOrg !== 'all' || hidePrivateEvents
                                                ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900'
                                                : showFilters
                                                    ? theme.viewButtonActive
                                                    : theme.viewButtonInactive
                                        }`}
                                        aria-pressed={showFilters}
                                        aria-label="Toggle organization filter panel"
                                        title={selectedOrg !== 'all' ? `Filtered by ${selectedOrg}` : 'No filters applied'}
                                    >
                                        <i className="fas fa-filter" aria-hidden="true"></i>
                                        <span className="text-xs leading-none">Filter</span>
                                    </button>
                                </div>
                                
                                {/* Navigation Controls */}
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={handleDecrement}
                                        className={`p-2 ${theme.navArrowHover} rounded-lg ${theme.text}`}
                                        aria-label="Previous"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>

                                    {viewMode === 'week' ? (
                                        <button
                                            onClick={() => {
                                                setShowWeekPicker(!showWeekPicker);
                                                processedSlugRef.current = null;
                                            }}
                                            className={`text-lg font-semibold ${theme.navArrowHover} ${theme.text} px-2 py-1 rounded-lg`}
                                        >
                                            {renderWeekRange()} <i className="fas fa-caret-down ml-2" aria-hidden="true"></i>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setShowMonthPicker(!showMonthPicker)}
                                            className={`text-lg font-semibold ${theme.navArrowHover} ${theme.text} px-2 py-1 rounded-lg`}
                                        >
                                            {renderMonthName()} <i className="fas fa-caret-down ml-2" aria-hidden="true"></i>
                                        </button>
                                    )}

                                    <button
                                        onClick={handleIncrement}
                                        className={`p-2 ${theme.navArrowHover} rounded-lg ${theme.text}`}
                                        aria-label="Next"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                    
                                </div>

                                {/* Month Picker */}
                                {showMonthPicker && (
                                    <div className={`absolute z-10 mt-2 ${theme.modalBg} rounded-lg shadow-lg p-4 border ${theme.border} top-full left-1/2 w-full -translate-x-1/2`}>
                                        <button
                                            onClick={gotoToday}
                                            className={`px-3 py-1 text-sm ${theme.actionButton} rounded-lg mb-2`}
                                        >
                                            Today
                                        </button>
                                        <div className="grid grid-cols-3 gap-2">
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <button
                                                    key={i+1}
                                                    onClick={() => {
                                                        setCurMonth(i+1);
                                                        setShowMonthPicker(false);
                                                        window.history.pushState({}, '', window.location.pathname);
                                                        setSelectedEventSlug(null);
                                                        processedSlugRef.current = null;
                                                    }}
                                                    className={`p-2 rounded-lg ${theme.text} ${
                                                        curMonth === i+1
                                                            ? theme.viewButtonActive
                                                            : new Date().getMonth() + 1 === i+1 && new Date().getFullYear() === curYear
                                                                ? theme.dayHeaderBg
                                                                : theme.pickerHover
                                                    }`}
                                                >
                                                    {new Date(0, i).toLocaleString('default', { month: 'short' })}
                                                </button>
                                            ))}
                                        </div>
                                        <div className={`flex items-center justify-center gap-2 mt-2 ${theme.text}`}>
                                            <button
                                                onClick={() => setCurYear(prevYear => prevYear - 1)}
                                                disabled={curYear === 2025}
                                                className={curYear === 2025 ? "opacity-50 cursor-not-allowed" : ""}
                                                aria-label={curYear === 2025 ? "Cannot go back before 2025" : "Previous year"}
                                            >
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                            <span>{curYear}</span>
                                            <button
                                                onClick={() => setCurYear(prevYear => prevYear + 1)}
                                                aria-label="Next year"
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Week Picker */}
                                {showWeekPicker && (
                                    <div className={`absolute z-50 mt-2 ${theme.modalBg} rounded-lg shadow-lg p-4 border ${theme.border} top-full left-1/2 transform -translate-x-1/2`}>
                                        <button
                                            onClick={gotoToday}
                                            className={`px-3 py-1 text-sm ${theme.actionButton} rounded-lg mb-2`}
                                        >
                                            Today
                                        </button>
                                        <div className="grid grid-cols-1 gap-1">
                                            {getWeeksInMonth(curYear, curMonth).map((weekStart, index) => {
                                                const weekEnd = new Date(weekStart);
                                                weekEnd.setDate(weekStart.getDate() + 6);

                                                const isCurrentWeek = weekStart.getTime() === currentWeek.getTime();
                                                const isCurrentMonth = weekStart.getMonth() === curMonth - 1;

                                                return (
                                                    <button
                                                        key={index}
                                                        onClick={() => {
                                                            setCurrentWeek(weekStart);
                                                            setShowWeekPicker(false);

                                                            const endOfSelectedWeek = new Date(weekStart);
                                                            endOfSelectedWeek.setDate(weekStart.getDate() + 6);
                                                            const selectedWeekMonth = weekStart.getMonth() + 1;
                                                            const selectedWeekYear = weekStart.getFullYear();
                                                            const endWeekMonth = endOfSelectedWeek.getMonth() + 1;
                                                            const endWeekYear = endOfSelectedWeek.getFullYear();

                                                            const daysInStartMonth = 7 - weekStart.getDay();
                                                            const daysInEndMonth = endOfSelectedWeek.getDay() + 1;

                                                            if (daysInStartMonth >= daysInEndMonth) {
                                                                if (selectedWeekMonth !== curMonth || selectedWeekYear !== curYear) {
                                                                    setValue(selectedWeekMonth);
                                                                    setYear(selectedWeekYear);
                                                                }
                                                            } else {
                                                                if (endWeekMonth !== curMonth || endWeekYear !== curYear) {
                                                                    setValue(endWeekMonth);
                                                                    setYear(endWeekYear);
                                                                }
                                                            }

                                                            window.history.pushState({}, '', window.location.pathname);
                                                            setSelectedEventSlug(null);
                                                            processedSlugRef.current = null;
                                                        }}
                                                        className={`p-2 rounded-lg text-left ${
                                                            isCurrentWeek
                                                                ? theme.viewButtonActive
                                                                : isCurrentMonth
                                                                    ? `${theme.text} ${theme.pickerHover}`
                                                                    : `${theme.secondaryText} ${theme.navArrowHover}`
                                                        }`}
                                                    >
                                                        {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Column 3: Top Nav, Brought to you by, and Beta Version */}
                            <div className="hidden md:flex flex-col items-end gap-2">

                                <div className="flex items-center gap-4 text-sm">
                                    <a href="/about" className={theme.navLink}>About</a>
                                    <a href="/community" className={theme.navLink}>Community</a>
                                    <a href="/subscribe" className={theme.navLink}>Subscribe</a>
                                    <a href="#" className={theme.navLink} onClick={() => setShowLegal(!showLegal)}>Legal</a>
                                    <button
                                        onClick={toggleTheme}
                                        className={`p-1.5 rounded-lg ${theme.viewButtonInactive}`}
                                        aria-label={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                        title={themeName === 'dark' ? 'Light mode' : 'Dark mode'}
                                    >
                                        <i className={`fas fa-${themeName === 'dark' ? 'sun' : 'moon'}`} aria-hidden="true"></i>
                                    </button>
                                </div>
                                <div className="hidden md:block w-full col-span-3 text-right items-end">
              <a target="_blank" rel="noopener noreferrer" href="https://www.mirability.com" className="flex justify-end">
                <img src="logo-black-transparent-300.png" alt="Mirability" className="max-h-12 object-contain mb-2" />
              </a>
              <span className="text-xs">Brought to you by <a target="_blank" rel="noopener noreferrer" href="https://www.mirability.com">Mirability<sup>SM</sup></a></span>
            </div>
  
                            </div>
                            
                        </div>

                        {/* Organization Filter */}
                        {showFilters && (
                            <div className={`rounded-lg shadow-md opacity-[.93] ${theme.gridBg} p-4 mt-4`}>
                                <OrganizationFilter
                                    organizations={organizations}
                                    selectedOrg={selectedOrg}
                                    onOrgChange={setSelectedOrg}
                                    theme={theme}
                                    showStarredOnly={showStarredOnly}
                                    onStarredChange={setShowStarredOnly}
                                    hidePrivateEvents={hidePrivateEvents}
                                    onHidePrivateChange={handleHidePrivateChange}
                                />
                            </div>
                        )}
                    </div>

                    {/* Loading State */}
                    {isLoading && !initialLoadComplete && (
                        <div className={`fixed inset-0 ${theme.modalOverlay} flex items-center justify-center z-50`}>
                            <div className={`${theme.loadingBg} ${theme.loadingText} p-4 rounded-lg`}>
                                <i className="fas fa-spinner fa-spin fa-3x fa-fw"></i>
                                <span className="sr-only">Loading events...</span>
                            </div>
                        </div>
                    )}

                    {/* Screen reader live region — always in DOM so announcements are reliable */}
                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                        {toast}
                    </div>

                    {/* Toast Message — visual only */}
                    {toast && (
                        <div aria-hidden="true" className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-200 text-yellow-900 px-6 py-3 rounded shadow-lg text-center font-semibold text-sm animate-fade-in-out">
                            {toast}
                        </div>
                    )}

                    {/* Dismissible disclaimer banner — shown on first visit only */}
                    {!disclaimerDismissed && (
                        <div className={`${theme.alertBackground} flex items-center justify-between px-4 py-1.5 text-sm`} role="alert">
                            <span>Note: Events are subject to change. Please verify with the event host.</span>
                            <button
                                onClick={dismissDisclaimer}
                                aria-label="Dismiss disclaimer"
                                className="ml-4 text-gray-600 hover:text-gray-900 shrink-0"
                            >
                                <i className="fas fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                    )}
                    <div className="flex-1 overflow-hidden min-h-0">

                        {viewMode === 'month' || viewMode === 'week' ? (
                            <div {...handlers} className="h-full">
                                                            <CalendarView
                                curMonth={curMonth}
                                curYear={curYear}
                                events={filteredEvents}
                                onModalStateChange={setIsModalOpen}
                                selectedEventSlug={selectedEventSlug}
                                onModalClose={handleModalClose}
                                isModalOpen={isModalOpen}
                                theme={theme}
                                onMonthChange={setCurMonth}
                                onYearChange={setCurYear}
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                currentWeek={currentWeek}
                                selectedEvent={selectedEvent}
                                onEventSelect={(event) => setSelectedEvent(event)}
                                onRightPaneChange={setIsRightPaneOpen}
                                isRightPaneOpen={isRightPaneOpen}
                                combinedEvents={combinedEvents}
                                clearSelectedEventSlug={clearSelectedEventSlug}
                            />
                            </div>
                        ) : viewMode === 'list' ? (
                            <EventListView
                                events={filteredEvents}
                                curMonth={curMonth}
                                curYear={curYear}
                                theme={theme}
                            />
                        ) : (
                            <EventMapView 
                                theme={theme}
                                events={filteredEvents}
                            />
                        )}
                    </div>
                </div>
            </div>

            {showLegal && (
                <footer className={`fixed ${theme.headerBg} ${theme.text} p-2 md:p-4 text-center bottom-0 left-0 right-0 z-10`} role="contentinfo">
                    <div className="container mx-auto">
                        
                        <nav className="mb-2 md:mb-4" role="navigation" aria-label="Footer navigation">
                            <div className="flex justify-center space-x-4 text-sm md:text-base">
                                <Link to="/privacy" className="hover:text-blue-700">Privacy</Link>
                                <Link to="/terms" className="hover:text-blue-700">Terms</Link>
                            </div>
                        </nav>
                        <div className="text-xs md:text-sm">
                            <p>ATLTech.events<sup>SM</sup> (atltech.events) and Event Horizon Media<sup>SM</sup> are the Service Marks of Mirability, LLC<sup>SM</sup>.</p>
                            <p>Copyright 2026 Mirability, LLC. All Rights Reserved.</p>
                        </div>
                    </div>
                </footer>
            )}

        </div>
    );
}

export default Home; 